import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Badge, Form, Spinner, Modal, Alert, ProgressBar } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import { listAgentRuns, getAgentRunDetail, createAgentRun, controlAgentRun } from '../../api/cases';
import { AgentRun, AgentRunDetail, AgentStep, AgentFinding, AgentRunStatus } from '../../types';
import { formatTimestamp } from '../../utils/dateFormatters';

interface CaseAgentProps {
  case_id: string;
  case_type?: string;
  initialRunId?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const CASE_TYPE_OPTIONS = ['general', 'divorce', 'fraud', 'tax'];

const TOOL_ICONS: Record<string, string> = {
  get_data_inventory:          '📁',
  get_entities:                '👥',
  cash_activity_report:        '💰',
  bank_statement_gap_analysis: '🔍',
  search_transactions:         '🔎',
  analyze_transactions:        '🤖',
  flag_finding:                '🚩',
  done:                        '✅',
};

const TOOL_LABELS: Record<string, string> = {
  get_data_inventory:          'Get Data Inventory',
  get_entities:                'Get Entities',
  cash_activity_report:        'Cash Activity Report',
  bank_statement_gap_analysis: 'Gap Analysis',
  search_transactions:         'Search Transactions',
  analyze_transactions:        'Analyze Transactions (AI)',
  flag_finding:                'Flag Finding',
  done:                        'Finish',
};

const SEVERITY_COLORS: Record<string, string> = {
  high:   'danger',
  medium: 'warning',
  low:    'success',
  info:   'secondary',
};

function statusBadge(status: AgentRunStatus) {
  const map: Record<AgentRunStatus, string> = {
    running:  'primary',
    paused:   'warning',
    complete: 'success',
    failed:   'danger',
    stopped:  'secondary',
  };
  return <Badge bg={map[status] ?? 'secondary'}>{status}</Badge>;
}

function isActive(status: AgentRunStatus) {
  return status === 'running' || status === 'paused';
}

/** Convert a Markdown string to safe HTML (headings, bold, lists, paragraphs). */
function markdownToHtml(md: string): string {
  if (!md) return '';
  return md
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/^#### (.+)$/gm, '<h6>$1</h6>')
    .replace(/^### (.+)$/gm, '<h5>$1</h5>')
    .replace(/^## (.+)$/gm, '<h4>$1</h4>')
    .replace(/^# (.+)$/gm, '<h3>$1</h3>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>\n?)+/gs, (m) => `<ul>${m}</ul>`)
    .replace(/\n{2,}/g, '</p><p>')
    .replace(/^(?!<[hul])(.+)$/gm, '$1')
    .replace(/^(.+)$/, '<p>$1</p>');
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface StepCardProps {
  step: AgentStep;
  isLatest: boolean;
  isRunning: boolean;
}

const StepCard: React.FC<StepCardProps> = ({ step, isLatest, isRunning }) => {
  const [expanded, setExpanded] = useState(isLatest);
  const icon  = TOOL_ICONS[step.tool_name] ?? '🔧';
  const label = TOOL_LABELS[step.tool_name] ?? step.tool_name;

  let args: Record<string, any> = {};
  try { args = JSON.parse(step.tool_args || '{}'); } catch {}

  // For analyze_transactions show the question prominently, otherwise show all args
  const isAnalyze = step.tool_name === 'analyze_transactions';
  const headerArgs = isAnalyze
    ? (args.question ? `"${args.question}"` : null)
    : Object.keys(args).length > 0
      ? Object.entries(args).map(([k, v]) => `${k}: ${JSON.stringify(v)}`).join(' · ')
      : null;

  // Args to show in expanded body (full, untruncated)
  const bodyArgs = Object.keys(args).length > 0
    ? Object.entries(args).map(([k, v]) => (
        <div key={k}>
          <span className="text-muted" style={{ userSelect: 'none' }}>{k}: </span>
          <span style={{ wordBreak: 'break-word' }}>{typeof v === 'string' ? v : JSON.stringify(v)}</span>
        </div>
      ))
    : null;

  return (
    <div
      className={`border rounded mb-2 ${step.flagged ? 'border-warning' : 'border-secondary'}`}
      style={{ fontSize: '0.875rem' }}
    >
      <div
        className="d-flex align-items-center px-3 py-2 gap-2"
        style={{ cursor: 'pointer', background: isLatest && isRunning ? '#f0f8ff' : undefined }}
        onClick={() => setExpanded(e => !e)}
      >
        <span style={{ fontSize: '1rem' }}>{icon}</span>
        <span className="fw-semibold flex-shrink-0">{label}</span>
        {headerArgs && (
          <span className="text-muted" style={{ fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
            {headerArgs}
          </span>
        )}
        {step.flagged && <Badge bg="warning" text="dark" className="ms-auto me-2 flex-shrink-0">finding</Badge>}
        {step.reused_analysis_id && <Badge bg="light" text="secondary" className="ms-auto me-2 flex-shrink-0">reused</Badge>}
        {isLatest && isRunning && <Spinner animation="border" size="sm" className="ms-auto flex-shrink-0" />}
        {(!isLatest || !isRunning) && (
          <span className="text-muted ms-auto flex-shrink-0" style={{ fontSize: '0.75rem' }}>
            {step.duration_ms ? `${(step.duration_ms / 1000).toFixed(1)}s` : ''}
          </span>
        )}
        <span className="text-muted flex-shrink-0" style={{ fontSize: '0.8rem' }}>
          {expanded ? '▲' : '▼'}
        </span>
      </div>

      {expanded && (
        <div className="px-3 pb-2 border-top" style={{ background: '#fafafa' }}>
          {/* Full args / prompt */}
          {bodyArgs && (
            <div className="mt-2">
              <small className="text-muted fw-semibold">{isAnalyze ? 'PROMPT / PARAMETERS' : 'PARAMETERS'}</small>
              <div
                className="mt-1 p-2 rounded"
                style={{ background: '#eef', fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
              >
                {bodyArgs}
              </div>
            </div>
          )}
          {step.reasoning && (
            <div className="mt-2">
              <small className="text-muted fw-semibold">REASONING</small>
              <div className="mt-1" style={{ whiteSpace: 'pre-wrap', color: '#333', fontSize: '0.85rem' }}>
                {step.reasoning}
              </div>
            </div>
          )}
          {step.result_summary && (
            <div className="mt-2">
              <small className="text-muted fw-semibold">RESULT</small>
              <div
                className="mt-1 p-2 rounded"
                style={{ background: '#eef', fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap', overflowY: 'auto', maxHeight: 500, wordBreak: 'break-word' }}
              >
                {step.result_summary}
              </div>
            </div>
          )}
          <div className="mt-1">
            <small className="text-muted">{formatTimestamp(step.timestamp)}</small>
          </div>
        </div>
      )}
    </div>
  );
};

interface FindingCardProps {
  finding: AgentFinding;
  caseId: string;
}

const FindingCard: React.FC<FindingCardProps> = ({ finding: f, caseId }) => {
  const [expanded, setExpanded] = useState(false);
  const color = SEVERITY_COLORS[f.severity] ?? 'secondary';
  const emoji = f.severity === 'high' ? '🚨' : f.severity === 'medium' ? '⚠️' : f.severity === 'low' ? '✅' : 'ℹ️';
  return (
    <div className={`border rounded border-${color} border-opacity-50`} style={{ fontSize: '0.875rem' }}>
      <div
        className="d-flex align-items-center gap-2 px-2 py-1"
        style={{ cursor: 'pointer' }}
        onClick={() => setExpanded(e => !e)}
      >
        <Badge bg={color} style={{ flexShrink: 0 }}>{f.severity}</Badge>
        <span className="fw-semibold" style={{ lineHeight: 1.3 }}>
          {emoji} {f.title}
        </span>
        {f.amount_involved && (
          <span className="text-muted ms-auto flex-shrink-0" style={{ fontSize: '0.8rem' }}>
            ${parseFloat(f.amount_involved).toLocaleString()}
          </span>
        )}
        <span className="text-muted flex-shrink-0" style={{ fontSize: '0.75rem' }}>{expanded ? '▲' : '▼'}</span>
      </div>
      {expanded && (
        <div className="px-2 pb-2 border-top" style={{ background: '#fafafa' }}>
          <div className="mt-2" style={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem', lineHeight: 1.5, wordBreak: 'break-word' }}>
            {f.detail}
          </div>
          {f.amount_involved && (
            <div className="mt-1 text-muted" style={{ fontSize: '0.8rem' }}>
              Amount involved: <strong>${parseFloat(f.amount_involved).toLocaleString()}</strong>
            </div>
          )}
          {f.source_links && f.source_links.length > 0 && (
            <div className="mt-2 d-flex flex-wrap gap-2">
              {f.source_links.map((link, i) => (
                <a
                  key={i}
                  href={`/case/${caseId}/file/${link.file_id}?page=${link.page_number}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-secondary btn-sm"
                  style={{ fontSize: '0.78rem' }}
                  onClick={e => e.stopPropagation()}
                >
                  📄 {link.label || 'View document'}
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface FindingBadgesProps {
  findings: AgentFinding[];
}

const FindingBadges: React.FC<FindingBadgesProps> = ({ findings }) => {
  if (!findings?.length) return null;
  return (
    <div className="d-flex flex-wrap gap-1 mt-2">
      {findings.map((f, i) => (
        <Badge
          key={i}
          bg={SEVERITY_COLORS[f.severity] ?? 'secondary'}
          title={f.detail}
          style={{ cursor: 'help', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {f.severity === 'high' ? '🚨 ' : f.severity === 'medium' ? '⚠️ ' : ''}
          {f.title}
          {f.amount_involved ? ` · $${parseFloat(f.amount_involved).toLocaleString()}` : ''}
        </Badge>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Run detail view
// ---------------------------------------------------------------------------

interface RunDetailProps {
  caseId: string;
  runId: string;
  onBack: () => void;
}

const RunDetail: React.FC<RunDetailProps> = ({ caseId, runId, onBack }) => {
  const auth = useAuth();
  const [detail, setDetail] = useState<AgentRunDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stepsEndRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const data = await getAgentRunDetail(caseId, runId, auth.user?.id_token);
      setDetail(data);
      setError(null);
    } catch (e: any) {
      setError(e.message ?? 'Failed to load run');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [caseId, runId, auth.user?.id_token]);

  useEffect(() => {
    load();
  }, [load]);

  // Auto-scroll steps to bottom when new steps arrive
  useEffect(() => {
    stepsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [detail?.steps?.length]);

  // Polling while active
  useEffect(() => {
    if (!detail) return;
    if (isActive(detail.run.status)) {
      pollRef.current = setInterval(() => load(true), 3000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [detail?.run.status, load]);

  const handleStop = async () => {
    if (!confirm('Stop this investigation?')) return;
    setActionLoading(true);
    try {
      await controlAgentRun(caseId, runId, 'stop', undefined, auth.user?.id_token);
      await load(true);
    } catch (e: any) {
      alert(`Stop failed: ${e.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleResume = async () => {
    if (!feedback.trim()) { alert('Please enter feedback before resuming.'); return; }
    setActionLoading(true);
    try {
      await controlAgentRun(caseId, runId, 'resume', feedback.trim(), auth.user?.id_token);
      setFeedback('');
      await load(true);
    } catch (e: any) {
      alert(`Resume failed: ${e.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <div className="py-4 text-center"><Spinner animation="border" /></div>;
  if (error)   return <Alert variant="danger">{error}</Alert>;
  if (!detail) return null;

  const { run, steps } = detail;
  const progress = Math.min(100, Math.round((run.step_count / run.max_steps) * 100));

  return (
    <div>
      {/* Header */}
      <div className="d-flex align-items-start gap-3 mb-3">
        <Button variant="link" size="sm" className="p-0 text-secondary text-decoration-none" onClick={onBack}>
          ← Back
        </Button>
        <div className="flex-grow-1">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {statusBadge(run.status)}
            <span className="fw-semibold">{run.objective}</span>
            <Badge bg="light" text="dark" className="border">{run.case_type}</Badge>
          </div>
          <div className="text-muted" style={{ fontSize: '0.8rem' }}>
            Started {formatTimestamp(run.created_at)}
            {run.completed_at && ` · Finished ${formatTimestamp(run.completed_at)}`}
            {run.created_by && ` · by ${run.created_by}`}
          </div>
        </div>
        <div className="d-flex gap-2">
          {run.status === 'running' && (
            <Button variant="outline-danger" size="sm" onClick={handleStop} disabled={actionLoading}>
              {actionLoading ? <Spinner size="sm" animation="border" /> : 'Stop'}
            </Button>
          )}
          {(run.final_report || run.conclusion) && (
            <Button variant="outline-primary" size="sm" onClick={() => setShowReport(true)}>
              View Report
            </Button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      {isActive(run.status) && (
        <div className="mb-3">
          <div className="d-flex justify-content-between" style={{ fontSize: '0.8rem' }}>
            <span className="text-muted">Step {run.step_count} of {run.max_steps}</span>
            {run.status === 'running' && (
              <span className="text-primary"><Spinner size="sm" animation="border" className="me-1" />Running…</span>
            )}
          </div>
          <ProgressBar now={progress} variant={run.status === 'paused' ? 'warning' : 'primary'} style={{ height: 4 }} />
        </div>
      )}

      {/* Findings */}
      {run.findings?.length > 0 && (
        <div className="mb-3 p-3 rounded border" style={{ background: '#fffbf0' }}>
          <div className="fw-semibold mb-1" style={{ fontSize: '0.875rem' }}>
            🚩 Findings ({run.findings.length})
          </div>
          <div className="d-flex flex-column gap-2">
            {run.findings.map((f, i) => (
              <FindingCard key={i} finding={f} caseId={caseId} />
            ))}
          </div>
        </div>
      )}

      {/* Pause / resume panel */}
      {run.status === 'paused' && (
        <Alert variant="warning" className="mb-3">
          <div className="fw-semibold">Investigation paused — waiting for your input</div>
          <Form.Control
            as="textarea"
            rows={2}
            className="mt-2"
            placeholder="Provide guidance or additional context for the agent…"
            value={feedback}
            onChange={e => setFeedback(e.target.value)}
          />
          <Button
            variant="warning"
            size="sm"
            className="mt-2"
            onClick={handleResume}
            disabled={actionLoading || !feedback.trim()}
          >
            {actionLoading ? <Spinner size="sm" animation="border" /> : 'Resume Investigation'}
          </Button>
        </Alert>
      )}

      {/* Error */}
      {run.status === 'failed' && run.error && (
        <Alert variant="danger" className="mb-3">
          <strong>Error:</strong> {run.error}
        </Alert>
      )}

      {/* Conclusion card */}
      {run.conclusion && (
        <div className="mb-3 p-3 border rounded" style={{ background: '#f0fff4' }}>
          <div className="fw-semibold mb-1">Conclusion</div>
          <div style={{ fontSize: '0.875rem' }}>{run.conclusion}</div>
        </div>
      )}

      {/* Steps */}
      <div className="fw-semibold mb-2" style={{ fontSize: '0.875rem' }}>
        Investigation Steps ({steps.length})
      </div>
      <div style={{ maxHeight: 520, overflowY: 'auto', paddingRight: 4 }}>
        {steps.length === 0 && isActive(run.status) && (
          <div className="text-muted py-3 text-center">
            <Spinner size="sm" animation="border" className="me-2" />
            Starting investigation…
          </div>
        )}
        {steps.map((step, idx) => (
          <StepCard
            key={`${step.step_number}-${step.tool_name}`}
            step={step}
            isLatest={idx === steps.length - 1}
            isRunning={run.status === 'running'}
          />
        ))}
        <div ref={stepsEndRef} />
      </div>

      {/* Full report modal */}
      <Modal show={showReport} onHide={() => setShowReport(false)} size="xl" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Investigation Report</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {run.final_report ? (
            <div
              style={{ fontFamily: "'Times New Roman', serif", lineHeight: 1.7, fontSize: '1rem' }}
              dangerouslySetInnerHTML={{ __html: markdownToHtml(run.final_report) }}
            />
          ) : (
            <p className="text-muted">No report generated yet.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={() => {
            const w = window.open('', '_blank');
            if (!w) { alert('Popup blocked. Please allow popups for this site.'); return; }
            w.document.write(`<html><head><title>Agent Report</title><style>body{font-family:'Times New Roman',serif;padding:32px;max-width:860px;margin:auto;line-height:1.7}</style></head><body>${markdownToHtml(run.final_report ?? '')}</body></html>`);
            w.document.close();
            w.focus();
            setTimeout(() => w.print(), 300);
          }}>
            Print / Export PDF
          </Button>
          <Button variant="secondary" onClick={() => setShowReport(false)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Create run form
// ---------------------------------------------------------------------------

interface CreateRunFormProps {
  caseId: string;
  defaultCaseType: string;
  onCreated: (runId: string) => void;
  onCancel: () => void;
}

const CreateRunForm: React.FC<CreateRunFormProps> = ({ caseId, defaultCaseType, onCreated, onCancel }) => {
  const auth = useAuth();
  const [objective, setObjective] = useState('');
  const [caseType, setCaseType] = useState(defaultCaseType || 'general');
  const [maxSteps, setMaxSteps] = useState(12);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!objective.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await createAgentRun(
        caseId,
        { objective: objective.trim(), case_type: caseType, max_steps: maxSteps },
        auth.user?.id_token
      );
      onCreated(result.run_id);
    } catch (err: any) {
      setError(err.response?.data?.error ?? err.message ?? 'Failed to start investigation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-3 border rounded mb-3" style={{ background: '#f8f9fa' }}>
      <h6 className="mb-3">New Investigation</h6>
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label className="small fw-semibold">Objective</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            placeholder="e.g. Identify hidden assets and suspicious transfers in the period 2022–2024"
            value={objective}
            onChange={e => setObjective(e.target.value)}
            required
            autoFocus
          />
          <Form.Text className="text-muted">
            Be specific — the agent will use this to focus its investigation.
          </Form.Text>
        </Form.Group>
        <div className="row g-3 mb-3">
          <div className="col-sm-6">
            <Form.Group>
              <Form.Label className="small fw-semibold">Investigation Type</Form.Label>
              <Form.Select value={caseType} onChange={e => setCaseType(e.target.value)} size="sm">
                {CASE_TYPE_OPTIONS.map(t => (
                  <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>
          <div className="col-sm-6">
            <Form.Group>
              <Form.Label className="small fw-semibold">Max Steps: {maxSteps}</Form.Label>
              <Form.Range
                min={4}
                max={20}
                step={1}
                value={maxSteps}
                onChange={e => setMaxSteps(parseInt(e.target.value))}
              />
              <Form.Text className="text-muted">Each step = one LLM tool call (~30–90s)</Form.Text>
            </Form.Group>
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button type="submit" variant="primary" size="sm" disabled={submitting || !objective.trim()}>
            {submitting ? <><Spinner size="sm" animation="border" className="me-1" />Starting…</> : '🔬 Start Investigation'}
          </Button>
          <Button type="button" variant="outline-secondary" size="sm" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </Form>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Runs list
// ---------------------------------------------------------------------------

interface RunsListProps {
  runs: AgentRun[];
  onSelect: (runId: string) => void;
  onNewRun: () => void;
  loading: boolean;
}

const RunsList: React.FC<RunsListProps> = ({ runs, onSelect, onNewRun, loading }) => {
  if (loading) return <div className="py-4 text-center"><Spinner animation="border" /></div>;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <span className="fw-semibold text-muted" style={{ fontSize: '0.875rem' }}>
          {runs.length === 0 ? 'No investigations yet' : `${runs.length} investigation${runs.length !== 1 ? 's' : ''}`}
        </span>
        <Button variant="primary" size="sm" onClick={onNewRun}>
          + New Investigation
        </Button>
      </div>

      {runs.length === 0 && (
        <div className="text-center py-5 text-muted">
          <div style={{ fontSize: '2.5rem' }}>🔬</div>
          <div className="mt-2">Launch an AI agent to autonomously investigate this case.</div>
          <div style={{ fontSize: '0.85rem' }}>The agent will examine documents, flag findings, and produce a forensic report.</div>
          <Button variant="primary" className="mt-3" onClick={onNewRun}>Start First Investigation</Button>
        </div>
      )}

      {runs.map(run => (
        <div
          key={run.run_id}
          className="border rounded p-3 mb-2 d-flex align-items-start gap-3"
          style={{ cursor: 'pointer', transition: 'background 0.15s' }}
          onClick={() => onSelect(run.run_id)}
          onMouseEnter={e => (e.currentTarget.style.background = '#f0f4ff')}
          onMouseLeave={e => (e.currentTarget.style.background = '')}
        >
          <div className="flex-grow-1 min-width-0">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {statusBadge(run.status)}
              <span className="fw-semibold" style={{ fontSize: '0.9rem' }}>{run.objective}</span>
            </div>
            <div className="text-muted mt-1" style={{ fontSize: '0.8rem' }}>
              {run.case_type} · {run.step_count}/{run.max_steps} steps · {formatTimestamp(run.created_at)}
            </div>
            <FindingBadges findings={run.findings ?? []} />
          </div>
          {isActive(run.status) && <Spinner animation="border" size="sm" className="mt-1" />}
          <span className="text-muted" style={{ fontSize: '1.1rem', marginTop: 2 }}>›</span>
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main CaseAgent component
// ---------------------------------------------------------------------------

type ViewMode = 'list' | 'create' | 'detail';

const CaseAgent: React.FC<CaseAgentProps> = ({ case_id, case_type, initialRunId }) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState<ViewMode>(initialRunId ? 'detail' : 'list');
  const [selectedRunId, setSelectedRunId] = useState<string | null>(initialRunId ?? null);
  const [runs, setRuns] = useState<AgentRun[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadRuns = useCallback(async (silent = false) => {
    if (!silent) setLoadingList(true);
    try {
      const data = await listAgentRuns(case_id, auth.user?.id_token);
      setRuns(data.runs ?? []);
      setListError(null);
    } catch (e: any) {
      setListError(e.message ?? 'Failed to load agent runs');
    } finally {
      if (!silent) setLoadingList(false);
    }
  }, [case_id, auth.user?.id_token]);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  // Poll list when we have active runs in list view
  useEffect(() => {
    if (view !== 'list') return;
    const hasActive = runs.some(r => isActive(r.status));
    if (hasActive) {
      pollRef.current = setInterval(() => loadRuns(true), 5000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [runs, view, loadRuns]);

  const handleSelectRun = (runId: string) => {
    setSelectedRunId(runId);
    setView('detail');
    navigate(`/case/${case_id}/agent/${runId}`);
  };

  const handleCreated = (runId: string) => {
    setSelectedRunId(runId);
    setView('detail');
    navigate(`/case/${case_id}/agent/${runId}`);
    loadRuns(true);
  };

  const handleBack = () => {
    setView('list');
    setSelectedRunId(null);
    navigate(`/case/${case_id}`);
    loadRuns(true);
  };

  return (
    <div className="mt-3">
      {listError && view === 'list' && (
        <Alert variant="danger" dismissible onClose={() => setListError(null)}>{listError}</Alert>
      )}

      {view === 'list' && (
        <RunsList
          runs={runs}
          onSelect={handleSelectRun}
          onNewRun={() => setView('create')}
          loading={loadingList}
        />
      )}

      {view === 'create' && (
        <>
          <Button variant="link" size="sm" className="p-0 text-secondary text-decoration-none mb-3" onClick={() => setView('list')}>
            ← Back to runs
          </Button>
          <CreateRunForm
            caseId={case_id}
            defaultCaseType={case_type ?? 'general'}
            onCreated={handleCreated}
            onCancel={() => setView('list')}
          />
        </>
      )}

      {view === 'detail' && selectedRunId && (
        <RunDetail
          caseId={case_id}
          runId={selectedRunId}
          onBack={handleBack}
        />
      )}
    </div>
  );
};

export default CaseAgent;
